import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { deliveryKeys, type Delivery } from './entity';
import type { DeliveriesPage } from './queries';
import { canBeSentOrDeleted } from './utils';

/**
 * Optimistic bulk-op marking. After send/retry/redeliver/delete the Ember
 * controller does NOT refetch — it flips a client-only `pending*` flag on the
 * affected rows, but only on rows that pass `canBeSentOrDeleted`. We reproduce
 * that: patch every cached list page (and detail slot) in place, gated by the
 * same predicate. No refetch (the list is `staleTime: 0`, so the next visit
 * reconciles with the server).
 */
export type PendingField = 'pendingSend' | 'pendingRetry' | 'pendingDelete';

export function markDeliveriesPending(
  qc: QueryClient,
  workspaceId: number,
  ids: readonly string[],
  field: PendingField,
): void {
  const idSet = new Set(ids);

  const listQueries = qc.getQueryCache().findAll({ queryKey: deliveryKeys.lists(workspaceId) });
  for (const query of listQueries) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const data = query.state.data as InfiniteData<DeliveriesPage> | undefined;
    if (data?.pages == null) continue;
    qc.setQueryData(query.queryKey, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        deliveries: page.deliveries.map((d) =>
          idSet.has(d.id) && canBeSentOrDeleted(d) ? { ...d, [field]: true } : d,
        ),
      })),
    });
  }

  for (const id of ids) {
    const detail = qc.getQueryData<Delivery>(deliveryKeys.detail(workspaceId, id));
    if (detail != null && canBeSentOrDeleted(detail)) {
      qc.setQueryData(deliveryKeys.detail(workspaceId, id), { ...detail, [field]: true });
    }
  }
}

/**
 * Keep the optimistic `pending*` flags across a list refetch ("load new", check now). The server
 * does not know about them, so a fresh page would drop them, and a row whose bulk action the
 * server has not processed yet would become selectable again. A flag carries over only while the
 * fresh row still passes `canBeSentOrDeleted`, the same gate that set it: once the server has
 * processed the action, the row's own state wins.
 */
export function carryPendingFlags(
  previous: InfiniteData<DeliveriesPage> | undefined,
  fresh: readonly Delivery[],
): readonly Delivery[] {
  const pendingById = new Map<string, Pick<Delivery, PendingField>>();
  for (const page of previous?.pages ?? []) {
    for (const d of page.deliveries) {
      if (d.pendingSend === true || d.pendingRetry === true || d.pendingDelete === true) {
        pendingById.set(d.id, {
          pendingSend: d.pendingSend,
          pendingRetry: d.pendingRetry,
          pendingDelete: d.pendingDelete,
        });
      }
    }
  }
  if (pendingById.size === 0) {
    return fresh;
  }
  return fresh.map((d) => {
    const pending = pendingById.get(d.id);
    return pending != null && canBeSentOrDeleted(d) ? { ...d, ...pending } : d;
  });
}
